import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import { OfflineError } from '../api/client.js';
import {
  type Course,
  type CreateCourseInput,
  listCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  clearCourses,
} from '../api/courses.js';
import { newTempId, type CoursePatch } from '../lib/offline/queue.js';
import { useOffline } from './offline.js';
import { currentStore } from './shop.js';

interface CoursesState {
  courses: Course[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  add: (input: Omit<CreateCourseInput, 'store'> | string) => Promise<Course>;
  toggle: (cid: string, done: boolean) => Promise<void>;
  rename: (cid: string, title: string) => Promise<void>;
  setQty: (cid: string, qty: number) => Promise<void>;
  remove: (cid: string) => Promise<void>;
  clearAll: () => Promise<void>;
  clearDone: () => Promise<void>;
}

function optimisticCourse(tempId: string, input: CreateCourseInput): Course {
  return {
    courseId: tempId,
    title: input.title ?? '',
    done: false,
    qty: input.qty ?? 1,
    catalogItemId: input.catalogItemId ?? null,
    store: input.store,
    createdAt: new Date().toISOString(),
  };
}

export const useCourses = create<CoursesState>((set, get) => ({
  courses: [],
  loading: false,
  error: null,
  /**
   * Charge la liste du magasin **courant**, lu dans `useShop` — pas reçu en argument :
   * `drain()` appelle `load()` sans connaître l'écran (ADR 0011 § 6).
   *
   * La liste est vidée avant l'appel : garder celle de l'autre magasin sous le nouveau
   * libellé, le temps du chargement, afficherait des articles qui n'y sont pas.
   */
  load: async () => {
    set({ courses: [], loading: true, error: null });
    try {
      set({ courses: await listCourses(currentStore()), loading: false });
    } catch (err) {
      set({ error: errorMessage(err), loading: false });
    }
  },

  add: async (input) => {
    const store = currentStore();
    const payload: CreateCourseInput =
      typeof input === 'string' ? { title: input, store } : { ...input, store };
    try {
      const c = await createCourse(payload);
      set({ courses: [...get().courses, c] });
      return c;
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      const tempId = newTempId();
      const c = optimisticCourse(tempId, payload);
      useOffline.getState().enqueue({ kind: 'course.create', id: tempId, input: payload });
      set({ courses: [...get().courses, c] });
      return c;
    }
  },

  toggle: (cid, done) => patchCourse(get, set, cid, { done }),
  rename: (cid, title) => patchCourse(get, set, cid, { title }),
  setQty: (cid, qty) => patchCourse(get, set, cid, { qty }),

  remove: async (cid) => {
    try {
      await deleteCourse(cid);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'course.delete', id: cid });
    }
    set({ courses: get().courses.filter((c) => c.courseId !== cid) });
  },

  clearAll: () => clear(get, set, false),
  clearDone: () => clear(get, set, true),
}));

async function patchCourse(
  get: () => CoursesState,
  set: (s: Partial<CoursesState>) => void,
  cid: string,
  patch: CoursePatch
): Promise<void> {
  try {
    const updated = await updateCourse(cid, patch);
    set({ courses: get().courses.map((c) => (c.courseId === cid ? updated : c)) });
  } catch (err) {
    if (!(err instanceof OfflineError)) throw err;
    useOffline.getState().enqueue({ kind: 'course.update', id: cid, patch });
    set({ courses: get().courses.map((c) => (c.courseId === cid ? { ...c, ...patch } : c)) });
  }
}

/**
 * Vider la liste, en ligne comme hors-ligne.
 *
 * En ligne, c'est le DELETE en masse : un seul aller-retour. Hors-ligne, la file ne
 * reçoit **pas** ce DELETE — son effet dépendrait de l'état serveur au moment du rejeu,
 * et vider « les articles cochés » une heure plus tard peut en emporter d'autres, sans
 * retour possible. Elle reçoit donc la liste des ids **figée au clic**, une suppression
 * par article ([ADR 0010](../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md) § 2).
 */
async function clear(
  get: () => CoursesState,
  set: (s: Partial<CoursesState>) => void,
  onlyDone: boolean
): Promise<void> {
  const targets = get().courses.filter((c) => !onlyDone || c.done);
  try {
    // `get().courses` ne contient que le magasin affiché : le DELETE en masse est borné
    // au même, jamais aux deux listes.
    await clearCourses(currentStore(), onlyDone);
  } catch (err) {
    if (!(err instanceof OfflineError)) throw err;
    for (const c of targets) {
      useOffline.getState().enqueue({ kind: 'course.delete', id: c.courseId });
    }
  }
  const removed = new Set(targets.map((c) => c.courseId));
  set({ courses: get().courses.filter((c) => !removed.has(c.courseId)) });
}
